export let styleSheet;
const styleCache = new Map();

/**
 * Dynamically generates class names from the styles, inserts them into a single stylesheet,
 * and returns an object mapping style keys to their respective class names.
 * This function also handles @keyframes rules (maybe).
 * @param {object} styles - Object containing style definitions.
 * @returns {object} - An object mapping each style key to its generated class name or animation name.
 */
export const buildStyles = (styles) => {
  const cacheKey = stringifyStyles(styles);
  if (styleCache.has(cacheKey)) {
    return styleCache.get(cacheKey);
  }
  if (!styleSheet) {
    const styleElement = document.createElement("style");
    document.head.appendChild(styleElement);
    styleSheet = styleElement.sheet;
  }
  const classes = {};
  const sheetLength = () => styleSheet.cssRules.length;

  Object.entries(styles).forEach(([key, style]) => {
    if (key.startsWith("@")) {
      if (key.startsWith("@keyframes")) {
        const animationName = "k-" + Math.random().toString(36).slice(2, 7);
        let rule = "";
        for (const [pct, props] of Object.entries(style)) {
          rule +=
            pct +
            "{" +
            Object.entries(props)
              .map(([p, v]) => toKababCase(p) + ":" + v + ";")
              .join("") +
            "}";
        }
        try {
          styleSheet.insertRule(
            "@keyframes " + animationName + "{" + rule + "}",
            sheetLength(),
          );
          classes[key] = animationName;
        } catch (e) {
          // we don't speak of this
        }
      }
      return;
    }

    const className = "c-" + key + "-" + Math.random().toString(36).slice(2, 7);
    const flat = flattenStyle(style);
    let baseRule = "";
    for (const [p, v] of Object.entries(flat)) {
      if (!p.startsWith("&")) {
        baseRule += toKababCase(p) + ":" + v + ";";
      }
    }
    styleSheet.insertRule(
      "." + className + "{" + baseRule + "}",
      sheetLength(),
    );

    for (const [p, v] of Object.entries(flat)) {
      if (p.startsWith("&")) {
        const pseudo = "." + className + p.slice(1);
        const pseudoRule = Object.entries(v)
          .map(([pp, vv]) => toKababCase(pp) + ":" + vv + ";")
          .join("");
        styleSheet.insertRule(pseudo + "{" + pseudoRule + "}", sheetLength());
      }
    }

    classes[key] = className;
  });

  styleCache.set(cacheKey, classes);
  return classes;
};

/**
 * Recursively flattens nested style objects into a single level for (maybe) quicker processing.
 * @param {object} obj - A potentially nested style object.
 * @returns {object} - A single-level style object with nested styles preserved as objects.
 */
const flattenStyle = (obj) => {
  const result = {};
  for (const key in obj) {
    const val = obj[key];
    if (val && typeof val === "object" && !Array.isArray(val)) {
      result[key] = flattenStyle(val); // Recursion!!!
    } else {
      result[key] = val;
    }
  }
  return result;
};

/**
 * Converts a camelCase CSS property to kebab-case.
 * @param {string} s - The camelCase property name.
 * @returns {string} - The kebab-case version of the property name.
 * @description chloe was here
 */
const toKababCase = (s) => s.replace(/[A-Z]/g, (l) => "-" + l.toLowerCase());

/**
 * Creates a unique string to represent style objects, used to detect and cache duplicates.
 * @param {*} obj - The object (style definition, arrays, or primitives) to convert into a string.
 * @returns {string} - A serialized, stable signature for the input object.
 */
const stringifyStyles = (obj) => {
  const type = typeof obj;
  if (obj == null) return "n";
  if (type === "boolean") return obj ? "b1" : "b0";
  if (type === "number") return "nm" + obj;
  if (type === "string") return "s" + obj;

  if (Array.isArray(obj)) {
    let r = "A["; // This is ass
    for (let i = 0; i < obj.length; i++) {
      if (i) r += ",";
      r += stringifyStyles(obj[i]); // Sorry
    }
    return r + "]";
  }

  const keys = Object.keys(obj).sort();
  let res = "O{";
  for (let i = 0; i < keys.length; i++) {
    if (i) res += ",";
    const k = keys[i];
    res += k + ":" + stringifyStyles(obj[k]);
  }
  return res + "}";
};
